import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import { API_BASE_URL } from '../config';
import { useSelectStyles } from '../utils/selectStyles';

const SectorSettingsModal = ({ isOpen, onClose, onSave, sector, sectors, selectedProject, fetchWithAuth }) => {
  const { t } = useTranslation();
  const [usernames, setUsernames] = useState([]);
  const [formData, setFormData] = useState({
    username: '',
    sector: ''
  });

  const styles = useSelectStyles();

  const fetchUsernames = useCallback(async () => {
    try {
      const response = await fetchWithAuth(
        `${API_BASE_URL}/api/columns/username?project=${selectedProject}`
      );

      const values = await response.json();

      // For other columns, transform to react-select format
      setUsernames(values.map(value => ({ value, label: value })));

    } catch (error) {
      console.error('Error fetching dimensions:', error);
    }
  }, [fetchWithAuth, selectedProject]);

  useEffect(() => {
    // no need to fetch usernames if we're editing an existing sector
    if (sector)
      return;

    fetchUsernames();
  }, [fetchUsernames]);

  // Get existing sectors
  const existingSectors = sectors.map(q => q.username);

  // Create options for react-select, filtering out existing questions
  const usernameOptions = usernames
    .filter(q => !existingSectors.includes(q.value));

  useEffect(() => {
    if (sector) {
      setFormData({
        username: sector.username || '',
        sector: sector.sector || ''
      });
    } else {
      setFormData({
        username: '',
        sector: ''
      });
    }

    // Add event listener for Escape key
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleEscape);

    // Cleanup listener on unmount
    return () => {
      window.removeEventListener('keydown', handleEscape);
    };
  }, [sector, onClose]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleUsernameSelect = (selectedOption) => {
    setFormData((prev) => ({
      ...prev,
      username: selectedOption.value,
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
        <h2 className="text-xl font-bold mb-4">
          {sector ? t('Edit Sector Assignment') : t('New Sector Assignment')}
        </h2>

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('Username')}
            </label>
            {sector ? (
              <input
                type="text"
                value={formData.username}
                className="w-full p-2 border rounded-md bg-gray-100 dark:bg-gray-700"
                disabled
              />
            ) : (
              <Select
                value={formData.username ? { value: formData.username, label: formData.username } : null}
                onChange={handleUsernameSelect}
                options={usernameOptions}
                className="w-full"
                isSearchable
                placeholder={t('Select a username...')}
                required
                styles={styles}
              />
            )}
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('Sector')}
            </label>
            <input
              type="text"
              name="sector"
              value={formData.sector}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700"
            />
          </div>

          <div className="flex justify-end space-x-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600"
            >
              {t('Cancel')}
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 dark:bg-blue-500 rounded-md hover:bg-blue-700 dark:hover:bg-blue-600"
            >
              {t('Save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SectorSettingsModal;